import { useEffect, useRef } from "react";
import { projects } from "@/mocks/projects";

export default function LatestProjects() {
  const cardsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("opacity-100", "translate-y-0");
            entry.target.classList.remove("opacity-0", "translate-y-8");
          }
        });
      },
      { threshold: 0.1 }
    );

    const cards = cardsRef.current?.children;
    if (cards) {
      Array.from(cards).forEach((card, index) => {
        card.classList.add("opacity-0", "translate-y-8", "transition-all", "duration-700");
        (card as HTMLElement).style.transitionDelay = `${index * 150}ms`;
        observer.observe(card);
      });
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section id="projects" className="py-20 md:py-28 bg-white">
      <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-4">
            <div>
              <span className="inline-block text-dark-400 text-xs font-semibold uppercase tracking-widest mb-4">
                Realizovani projekti
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-dark-900 leading-tight">
                Najnovije instalacije
              </h2>
            </div>
            <p className="text-dark-500 text-sm max-w-md">
              Pogledajte neke od naših najnovijih projekata — svaka instalacija je prilagođena specifičnim potrebama klijenta
            </p>
          </div>

          <div ref={cardsRef} className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {projects.map((project, index) => (
              <div
                key={index}
                className="group rounded-2xl border border-dark-100 overflow-hidden bg-white hover:-translate-y-1 transition-all duration-300"
              >
                <div className="aspect-[16/10] bg-dark-100 overflow-hidden">
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <div className="p-5 md:p-6">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="text-xs font-semibold bg-accent-50 text-accent-700 px-2.5 py-1 rounded-full">
                      {project.type}
                    </span>
                    <span className="text-xs text-dark-400 flex items-center gap-1">
                      <i className="ri-map-pin-line"></i>
                      {project.location}
                    </span>
                  </div>
                  <h3 className="text-lg font-bold text-dark-900 mb-3 group-hover:text-brand-600 transition-colors">
                    {project.title}
                  </h3>
                  <p className="text-dark-500 text-sm leading-relaxed mb-5 line-clamp-3">
                    {project.description}
                  </p>
                  <div className="flex items-center gap-4 border-t border-dark-100 pt-4">
                    {project.stats.map((stat, i) => (
                      <div key={i} className="text-center">
                        <div className="text-lg font-bold text-brand-600">{stat.value}</div>
                        <div className="text-xs text-dark-400">{stat.label}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}